import React, { useContext } from 'react';
import { NodeProps } from 'reactflow';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { ReactComponent as CircleArrowRight } from '../../../../../images/icons/circle-arrow-right-solid.svg';
import { getNullSelectOption, Select } from '../../../../../shared/form/inputs/Select';
import { PumpFormModel } from '../../GetDataForQuoteOrDesignForm';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { DeletePopover } from '../DeletePopover';
import { useTree } from '../map-helpers/hooks/useTree';
import { getCapacityString, generatedPressureString } from '../map-helpers/mapHelpers';
import { PumpMapNodeData } from '../mapNode';
import { NukePopover } from '../NukePopover';
import { PumpTreeNode, TreeNode, TreeNodeType } from '../treeNode';
import { MapFlowNode } from './MapFlowNode';

export const Pump = ({
  data: {
    pump,
    treeNodeId,
    indexCircuitStats: { requiredPressureInKilopascals, availablePressureInKilopascals, pumpCapacityPercentage },
    generatedPressureInKilopascals,
    state,
    markedForDeletion,
  },
}: NodeProps<PumpMapNodeData>) => {
  const { tree, replaceNode } = useTree();
  const { referenceData } = assertNotNull(useContext(QuoteOrDesignFormContext));
  const currentPump =
    referenceData.unitPumpOptions.find(pumpOption => pumpOption.value?.displayName === pump)?.value ??
    getNullSelectOption<PumpFormModel>().value;

  const onChangePump = (newPump: PumpFormModel | null) => {
    const nodeToReplace = tree[treeNodeId];
    if (nodeToReplace.nodeType !== TreeNodeType.Pump) {
      return;
    }

    const newNode: TreeNode = {
      ...nodeToReplace,
      fieldData: {
        pump: newPump?.displayName ?? null,
        includesBlendingValve: newPump?.includesBlendingValve ?? null,
      },
    };
    replaceNode(treeNodeId, newNode);
  };

  const generatedPressure = generatedPressureString(generatedPressureInKilopascals);
  const capacity = getCapacityString({
    requiredPressureInKilopascals,
    availablePressureInKilopascals,
    pumpCapacityPercentage,
  });

  return (
    <MapFlowNode
      icon={<PumpIcon />}
      stats={[generatedPressure, capacity]}
      hasSource={true}
      hasTarget={true}
      title="Pump"
      state={state}
      markedForDeletion={markedForDeletion}
      popoverButtons={
        <>
          <NukePopover nodeId={treeNodeId} />
          <DeletePopover node={tree[treeNodeId] as PumpTreeNode} />
        </>
      }
    >
      <Select
        name="pump"
        value={currentPump}
        options={referenceData.unitPumpOptions}
        isSmall={true}
        onChange={onChangePump}
      />
    </MapFlowNode>
  );
};

const PumpIcon = styled(CircleArrowRight)`
  height: 1em;
  width: 1em;
`;
